// client/src/hooks/useNavItems.js
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import useAlert from './useAlert';

export const useNavItems = () => {
    const [navItems, setNavItems] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { setAlert } = useAlert();

    const fetchCatalog = useCallback(async () => {
        try {
            const response = await api.get('/admin/institucional/nav-items/catalog');
            setCatalog(response.data.data || []);
        } catch (err) {
            console.error('Erro ao buscar catálogo de páginas internas:', err);
        }
    }, []);

    const fetchNavItems = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/institucional/nav-items');
            const sorted = response.data.data.sort((a, b) => a.order - b.order);
            setNavItems(sorted);
            setError(null);
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao buscar itens de navegação';
            setError(errorMessage);
            setAlert(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    }, [setAlert]);

    const getNavItem = useCallback(async (id) => {
        try {
            const response = await api.get(`/admin/institucional/nav-items/${id}`);
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao buscar o item de navegação';
            setAlert(errorMessage, 'error');
            throw new Error(errorMessage);
        }
    }, [setAlert]);

    const createNavItem = useCallback(async (itemData) => {
        try {
            const response = await api.post('/admin/institucional/nav-items', itemData);
            setNavItems(prev => [...prev, response.data.data].sort((a, b) => a.order - b.order));
            setAlert('Item de navegação criado com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao criar item de navegação';
            setAlert(errorMessage, 'error');
            throw new Error(errorMessage);
        }
    }, [setAlert]);

    const updateNavItem = useCallback(async (id, itemData) => {
        try {
            const response = await api.put(`/admin/institucional/nav-items/${id}`, itemData);
            setNavItems(prev => prev.map(item =>
                item._id === id ? response.data.data : item
            ).sort((a, b) => a.order - b.order));
            setAlert('Item de navegação atualizado com sucesso!', 'success');
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar item de navegação';
            setAlert(errorMessage, 'error');
            throw new Error(errorMessage);
        }
    }, [setAlert]);

    const deleteNavItem = useCallback(async (id) => {
        try {
            await api.delete(`/admin/institucional/nav-items/${id}`);
            setNavItems(prev => prev.filter(item => item._id !== id));
            setAlert('Item de navegação excluído com sucesso!', 'success');
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao deletar item de navegação';
            setAlert(errorMessage, 'error');
            throw new Error(errorMessage);
        }
    }, [setAlert]);

    const updateNavItemsOrder = useCallback(async (orderedItems) => {
        try {
            const itemsToUpdate = orderedItems.map((item, index) => ({ _id: item._id, order: index }));
            const response = await api.put('/admin/institucional/nav-items/update-order', {
                navItems: itemsToUpdate
            });
            setNavItems(orderedItems);
            setAlert('Ordem dos itens de navegação atualizada!', 'success');
            return response.data;
        } catch (err) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar ordem dos itens de navegação';
            setAlert(errorMessage, 'error');
            throw new Error(errorMessage);
        }
    }, [setAlert]);

    useEffect(() => {
        fetchNavItems();
        fetchCatalog();
    }, [fetchNavItems, fetchCatalog]);

    return {
        navItems,
        setNavItems,
        catalog,
        loading,
        error,
        getNavItem,
        createNavItem,
        updateNavItem,
        deleteNavItem,
        updateNavItemsOrder,
        refetch: fetchNavItems
    };
};
